import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';

export interface Passkey {
  created_at: string;
  id: string;
  last_used_at: null | string;
  name: string;
}

interface OptionsResponse {
  options: string;
  state: string;
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
}

/** Whether this browser can do WebAuthn at all. */
export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && Boolean(window.PublicKeyCredential) && Boolean(navigator.credentials);
}

export function useDeletePasskey() {
  const queryClient = useQueryClient();
  return useMutation({
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['passkeys'] }),
    mutationFn: async (id: string) => {
      await clientApi.delete(`/api/v1/auth/passkeys/${id}`);
    },
  });
}

export function usePasskeys(enabled: boolean) {
  return useQuery({
    enabled,
    queryKey: ['passkeys'],
    queryFn: async () => {
      const { data } = await clientApi.get<{ passkeys: Passkey[] }>('/api/v1/auth/passkeys');
      return data.passkeys;
    },
  });
}

/** Sign in with a passkey. Usernameless — the authenticator offers the account. */
export function usePasskeySignIn() {
  return useMutation({
    mutationFn: async () => {
      const { data: opts } = await clientApi.post<OptionsResponse>('/api/v1/auth/passkeys/options', {});
      const credential = (await navigator.credentials.get({
        publicKey: decodeRequestOptions(opts.options),
      })) as null | PublicKeyCredential;
      if (!credential) throw new Error('cancelled');

      const { data } = await clientApi.post<TokenResponse>('/api/v1/auth/passkeys/verify', {
        state: opts.state,
        credential: encodeCredential(credential),
      });
      return data;
    },
  });
}

/** Add a passkey to the signed-in account. */
export function useRegisterPasskey() {
  const queryClient = useQueryClient();
  return useMutation({
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['passkeys'] }),
    mutationFn: async (name: string) => {
      const { data: opts } = await clientApi.post<OptionsResponse>('/api/v1/auth/passkeys/register/options', {});
      const credential = (await navigator.credentials.create({
        publicKey: decodeCreationOptions(opts.options),
      })) as null | PublicKeyCredential;
      if (!credential) throw new Error('cancelled');

      await clientApi.post('/api/v1/auth/passkeys/register', {
        name,
        state: opts.state,
        credential: encodeCredential(credential),
      });
    },
  });
}

/**
 * WebAuthn speaks ArrayBuffer; JSON does not. The spec's own wire encoding is
 * base64url, which is what the server sends and expects back.
 *
 * Hand-rolled rather than using PublicKeyCredential.parseCreationOptionsFromJSON:
 * that landed in Chrome 119 and Safari 17.4, and a clinician on a slightly older
 * browser would get an unexplained failure at the moment they try to sign in.
 */
function b64urlToBuffer(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '='));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToB64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Decode the server's JSON options into what navigator.credentials expects. */
function decodeCreationOptions(json: string): PublicKeyCredentialCreationOptions {
  const o = JSON.parse(json);
  return {
    ...o,
    challenge: b64urlToBuffer(o.challenge),
    user: { ...o.user, id: b64urlToBuffer(o.user.id) },
    excludeCredentials: (o.excludeCredentials ?? []).map((c: { id: string }) => ({
      ...c,
      id: b64urlToBuffer(c.id),
    })),
  };
}

function decodeRequestOptions(json: string): PublicKeyCredentialRequestOptions {
  const o = JSON.parse(json);
  return {
    ...o,
    challenge: b64urlToBuffer(o.challenge),
    allowCredentials: (o.allowCredentials ?? []).map((c: { id: string }) => ({
      ...c,
      id: b64urlToBuffer(c.id),
    })),
  };
}

function encodeCredential(credential: PublicKeyCredential) {
  const response = credential.response as AuthenticatorAssertionResponse & AuthenticatorAttestationResponse;
  const encoded: Record<string, unknown> = {
    id: credential.id,
    type: credential.type,
    rawId: bufferToB64url(credential.rawId),
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToB64url(response.clientDataJSON),
    },
  };
  const r = encoded.response as Record<string, unknown>;
  if (response.attestationObject) {
    r.attestationObject = bufferToB64url(response.attestationObject);
    // Hints for the next sign-in. Absent on some authenticators, hence the guard.
    r.transports = response.getTransports?.() ?? [];
  }
  if (response.authenticatorData) {
    r.authenticatorData = bufferToB64url(response.authenticatorData);
    r.signature = bufferToB64url(response.signature);
    r.userHandle = response.userHandle ? bufferToB64url(response.userHandle) : null;
  }
  return encoded;
}
